/**
 * Runtime Configuration Loader
 *
 * Loads configuration at runtime without rebuilding the frontend bundle.
 *
 * Priority (highest wins):
 * 1. window.__CONFIG__ from public/config.js  — Azure DevOps pipeline injects values per stage
 *                                                (Test stage / Production stage). Always wins for
 *                                                deployed environments.
 * 2. REACT_APP_* environment variables         — CRA bakes these in at build/start time.
 *                                                Active for local development via .env.development.
 *                                                .env.production serves as a safety net when
 *                                                config.js token replacement has not run yet.
 * 3. public/config.json                        — Static auth defaults (clientId, tenantId, etc.)
 *                                                committed to the repo; never contains tokens.
 * 4. DEFAULT_RUNTIME_CONFIG                    — In-code emergency fallback.
 *
 * Environment summary:
 *   development  →  npm start  →  .env.development + setupProxy.js (proxies /api to localhost)
 *   test         →  npm build  →  config.js injected by Azure DevOps Test stage pipeline
 *   production   →  npm build  →  config.js injected by Azure DevOps Production stage pipeline
 */

const DEFAULT_RUNTIME_CONFIG = {
  basePath: '',
  apiBasePath: '',
  clientId: '8ffc435e-c2e8-40e0-9880-761fe4dfa304',
  tenantId: '69b7a791-5a9c-4661-b6cf-1c6fb3d271c6',
  authorityBaseUrl: 'https://login.microsoftonline.com',
  apiAppIdUri: 'api://e161a8c9-41d5-4f78-8cbe-36aa7e6e2f09',
  loginScopes: ['User.Read'],
  apiScopes: ['api://e161a8c9-41d5-4f78-8cbe-36aa7e6e2f09/access_as_user'],
  adminRole: 'AppAdmin',
};

let runtimeConfig = { ...DEFAULT_RUNTIME_CONFIG };
let isLoaded = false;

const getConfigUrl = () => {
  const baseUrl = process.env.PUBLIC_URL || '';
  return `${baseUrl}/config.json`;
};

const isUnresolvedToken = (value) => {
  return typeof value === 'string' && /^#\{[^}]+\}#$/.test(value.trim());
};

const sanitizeTokenizedValues = (source) => {
  if (!isPlainObject(source)) {
    return {};
  }

  return Object.entries(source).reduce((acc, [key, value]) => {
    if (!isUnresolvedToken(value)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const isPlainObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Reads REACT_APP_* environment variables baked in by CRA at build/start time.
 *
 * In development  (npm start):  values come from .env.development / .env.local
 * In test/prod builds (npm run build on CI): values come from .env.production
 *   unless the CI explicitly overrides them — but window.__CONFIG__ (config.js)
 *   always beats this layer for deployed environments.
 *
 * Only keys with a non-empty value are included so that an undefined or empty
 * REACT_APP_* variable does not accidentally override a higher-fidelity source.
 *
 * Variable → config key mapping:
 *   REACT_APP_BASE_PATH                       → basePath
 *   REACT_APP_API_BASE_PATH                   → apiBasePath
 *   REACT_APP_APP_INSIGHTS_CONNECTION_STRING  → appInsightsConnectionString
 *   REACT_APP_CLIENT_ID                       → clientId
 *   REACT_APP_TENANT_ID                       → tenantId
 *   REACT_APP_API_APP_ID_URI                  → apiAppIdUri
 */
const getProcessEnvConfig = () => {
  const envMap = {
    REACT_APP_BASE_PATH: 'basePath',
    REACT_APP_API_BASE_PATH: 'apiBasePath',
    REACT_APP_APP_INSIGHTS_CONNECTION_STRING: 'appInsightsConnectionString',
    REACT_APP_CLIENT_ID: 'clientId',
    REACT_APP_TENANT_ID: 'tenantId',
    REACT_APP_API_APP_ID_URI: 'apiAppIdUri',
  };

  return Object.entries(envMap).reduce((acc, [envKey, configKey]) => {
    const value = process.env[envKey];
    if (value !== undefined && value !== '') {
      acc[configKey] = value;
    }
    return acc;
  }, {});
};

const normalizeConfig = (source) => {
  if (!isPlainObject(source)) {
    return {};
  }

  // Support both flat and grouped auth settings.
  const auth = isPlainObject(source.auth) ? source.auth : {};

  return {
    ...source,
    clientId: source.clientId || auth.clientId,
    tenantId: source.tenantId || auth.tenantId,
    authorityBaseUrl: source.authorityBaseUrl || auth.authorityBaseUrl,
    apiAppIdUri: source.apiAppIdUri || auth.apiAppIdUri,
    loginScopes: source.loginScopes || auth.loginScopes,
    apiScopes: source.apiScopes || auth.apiScopes,
    scopes: source.scopes || auth.scopes,
    adminRole: source.adminRole || auth.adminRole,
  };
};

export const loadRuntimeConfig = async () => {
  if (isLoaded) {
    return runtimeConfig;
  }

  const rawWindowConfig = sanitizeTokenizedValues(
    typeof window !== 'undefined' ? window.__CONFIG__ : null
  );
  const windowConfig = normalizeConfig(rawWindowConfig);

  let baseJsonConfig = {};

  try {
    const response = await fetch(getConfigUrl(), { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Unable to load config.json (HTTP ${response.status})`);
    }

    const json = await response.json();
    baseJsonConfig = normalizeConfig(sanitizeTokenizedValues(json));
  } catch (error) {
    // Keep app booting if base config is unavailable.
    console.warn('[RuntimeConfig] Unable to load base config.json.', error);
  }

  runtimeConfig = {
    ...DEFAULT_RUNTIME_CONFIG,   // 4. in-code emergency fallback
    ...baseJsonConfig,           // 3. public/config.json — static auth defaults
    ...getProcessEnvConfig(),    // 2. REACT_APP_* — .env.development (dev) / .env.production (build)
    ...windowConfig,             // 1. window.__CONFIG__ — pipeline-injected per stage (always wins)
  };

  isLoaded = true;
  return runtimeConfig;
};

export const getRuntimeConfig = () => runtimeConfig;

export const getConfigValue = (key, fallback) => {
  const value = runtimeConfig[key];
  return value === undefined || value === null || value === '' ? fallback : value;
};

export const getConfiguredScopes = () => {
  const configuredScopes = getConfigValue('scopes', []);
  if (Array.isArray(configuredScopes) && configuredScopes.length > 0) {
    return configuredScopes;
  }

  if (typeof configuredScopes === 'string' && configuredScopes.trim()) {
    return configuredScopes
      .split(',')
      .map((scope) => scope.trim())
      .filter(Boolean);
  }

  const apiAppIdUri = getConfigValue('apiAppIdUri', DEFAULT_RUNTIME_CONFIG.apiAppIdUri);
  return ['User.Read', `${apiAppIdUri}/access_as_user`];
};

const normalizeScopes = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((scope) => scope.trim())
      .filter(Boolean);
  }

  return [];
};

export const getConfiguredLoginScopes = () => {
  const configuredLoginScopes = normalizeScopes(getConfigValue('loginScopes', []));
  if (configuredLoginScopes.length > 0) {
    return configuredLoginScopes;
  }

  // Backward compatibility for old single "scopes" config.
  const legacyScopes = normalizeScopes(getConfigValue('scopes', []));
  return legacyScopes.filter((scope) => scope.toLowerCase() === 'user.read');
};

export const getConfiguredApiScopes = () => {
  const configuredApiScopes = normalizeScopes(getConfigValue('apiScopes', []));
  if (configuredApiScopes.length > 0) {
    return configuredApiScopes;
  }

  const apiAppIdUri = getConfigValue('apiAppIdUri', DEFAULT_RUNTIME_CONFIG.apiAppIdUri);

  // Backward compatibility for old single "scopes" config.
  const legacyScopes = normalizeScopes(getConfigValue('scopes', []));
  const apiLegacyScopes = legacyScopes.filter((scope) => scope.startsWith(`${apiAppIdUri}/`));
  if (apiLegacyScopes.length > 0) {
    return apiLegacyScopes;
  }

  return [`${apiAppIdUri}/access_as_user`];
};

const runtimeConfigApi = {
  loadRuntimeConfig,
  getRuntimeConfig,
  getConfigValue,
  getConfiguredScopes,
  getConfiguredLoginScopes,
  getConfiguredApiScopes,
};

export default runtimeConfigApi;